import { ChangeEvent, FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { api } from "./api";
import { backfillMediaThumbnails } from "./mediaBackfill";
import "./photos.css";

type MediaCategory = "meal-images" | "body-images" | "progress-photos";

type MediaRecord = {
  id: string;
  fileKey: string;
  category: MediaCategory;
  filename: string | null;
  mimeType: string | null;
  fileSize: number | null;
  relatedType: string | null;
  relatedId: string | null;
  createdAt: string;
};

const PAGE_SIZE = 20;

const labels: Record<MediaCategory, string> = {
  "meal-images": "食事写真",
  "body-images": "体型記録",
  "progress-photos": "経過写真",
};

function fileSize(value: number | null): string {
  if (!value) return "サイズ不明";
  if (value < 1024 * 1024) return `${Math.max(1, Math.round(value / 1024))} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function photoDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("ja-JP", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function photoUrl(fileKey: string): string {
  return `/api/app/media/${encodeURIComponent(fileKey)}`;
}

export function PhotosPage() {
  const [media, setMedia] = useState<MediaRecord[]>([]);
  const [filter, setFilter] = useState<"all" | MediaCategory>("all");
  const [category, setCategory] = useState<MediaCategory>("progress-photos");
  const [file, setFile] = useState<File | null>(null);
  const [inputKey, setInputKey] = useState(0);
  const [displayLimit, setDisplayLimit] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [connected, setConnected] = useState<boolean | null>(null);

  const previewUrl = useMemo(() => file ? URL.createObjectURL(file) : null, [file]);

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const loadMedia = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await api<{ ok: true; media: MediaRecord[] }>("/api/app/media");
      setMedia(result.media);
      void backfillMediaThumbnails(result.media, (created) => {
        setMedia((current) => current.some((item) => item.fileKey === created.fileKey)
          ? current
          : [created as MediaRecord, ...current]);
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "写真を読み込めませんでした。");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadMedia();
    api<{ connected?: boolean; remoteOk?: boolean }>("/api/app/media/status")
      .then((status) => setConnected(Boolean(status.connected && status.remoteOk)))
      .catch(() => setConnected(false));
  }, [loadMedia]);

  const originals = useMemo(
    () => media.filter((item) => item.relatedType !== "media-thumbnail"),
    [media],
  );
  const visible = useMemo(
    () => filter === "all" ? originals : originals.filter((item) => item.category === filter),
    [filter, originals],
  );
  const displayed = useMemo(() => visible.slice(0, displayLimit), [displayLimit, visible]);

  function selectFilter(value: "all" | MediaCategory) {
    setFilter(value);
    setDisplayLimit(PAGE_SIZE);
  }

  function chooseFile(event: ChangeEvent<HTMLInputElement>) {
    setMessage("");
    setError("");
    const selected = event.target.files?.[0] ?? null;
    if (!selected) {
      setFile(null);
      return;
    }
    if (selected.type && !selected.type.startsWith("image/")) {
      setError("画像ファイルを選択してください。");
      event.target.value = "";
      return;
    }
    if (selected.size > 25 * 1024 * 1024) {
      setError("写真は25MB以下にしてください。");
      event.target.value = "";
      return;
    }
    setFile(selected);
  }

  async function upload(event: FormEvent) {
    event.preventDefault();
    if (!file) {
      setError("アップロードする写真を選択してください。");
      return;
    }
    setUploading(true);
    setError("");
    setMessage("元画像と軽量サムネイルを保存しています…");
    try {
      const form = new FormData();
      form.set("file", file);
      form.set("category", category);
      await api<{ ok: true; media: MediaRecord }>("/api/app/media", {
        method: "POST",
        body: form,
      });
      setFilter(category);
      setDisplayLimit(PAGE_SIZE);
      setFile(null);
      setInputKey((current) => current + 1);
      await loadMedia();
      setMessage(`${labels[category]}として保存しました。次回から軽量表示されます。`);
      setConnected(true);
    } catch (err) {
      setMessage("");
      setError(err instanceof Error ? err.message : "写真を保存できませんでした。");
    } finally {
      setUploading(false);
    }
  }

  async function remove(item: MediaRecord) {
    if (!window.confirm(`「${item.filename || "この写真"}」を削除しますか？`)) return;
    setDeleting(item.fileKey);
    setError("");
    setMessage("");
    const thumbnail = media.find((value) =>
      value.relatedType === "media-thumbnail" && value.relatedId === item.fileKey,
    );
    try {
      if (thumbnail) {
        await api(`/api/app/media/${encodeURIComponent(thumbnail.fileKey)}`, { method: "DELETE" })
          .catch(() => undefined);
      }
      await api(`/api/app/media/${encodeURIComponent(item.fileKey)}`, { method: "DELETE" });
      setMedia((current) => current.filter((value) =>
        value.fileKey !== item.fileKey && value.fileKey !== thumbnail?.fileKey,
      ));
      navigator.serviceWorker?.controller?.postMessage({ type: "media-map-refresh", fileKey: item.fileKey });
      setMessage("写真と軽量サムネイルを削除しました。");
    } catch (err) {
      setError(err instanceof Error ? err.message : "写真を削除できませんでした。");
    } finally {
      setDeleting(null);
    }
  }

  return (
    <>
      <form className="panel photos-upload" onSubmit={upload}>
        <div className="panel-heading photos-heading">
          <div>
            <p className="eyebrow">PHOTO STORAGE</p>
            <h2>写真を追加</h2>
          </div>
          <span className={`status-chip ${connected === false ? "photos-status-error" : ""}`}>
            {connected === null ? "接続確認中" : connected ? "pCloud接続済み" : "接続エラー"}
          </span>
        </div>
        <p className="photos-help">元画像はpCloudへ保存し、一覧用の軽量サムネイルも自動作成します。</p>

        <div className="photos-form-grid">
          <label className="field">
            <span>写真の種類</span>
            <select value={category} onChange={(event) => setCategory(event.target.value as MediaCategory)}>
              {(Object.keys(labels) as MediaCategory[]).map((key) => (
                <option key={key} value={key}>{labels[key]}</option>
              ))}
            </select>
          </label>
          <label className="photos-picker">
            <span aria-hidden="true">＋</span>
            <strong>{file ? "別の写真を選ぶ" : "写真を選ぶ・撮影する"}</strong>
            <small>JPEG・PNG・HEICなど／25MBまで</small>
            <input key={inputKey} type="file" accept="image/*" onChange={chooseFile} />
          </label>
        </div>

        {previewUrl && file && (
          <div className="photos-preview">
            <img src={previewUrl} alt="アップロード前の確認" />
            <div>
              <strong>{file.name || "選択した写真"}</strong>
              <span>{fileSize(file.size)}</span>
            </div>
          </div>
        )}

        <button className="button primary" disabled={uploading || !file}>
          {uploading ? "保存中…" : "この写真を保存"}
        </button>
        {message && <p className="form-message" role="status">{message}</p>}
        {error && <p className="form-error" role="alert">{error}</p>}
      </form>

      <section className="panel photos-library">
        <div className="panel-heading photos-heading">
          <div>
            <p className="eyebrow">LIBRARY</p>
            <h2>保存した写真</h2>
          </div>
          <span className="photos-count">{visible.length}枚</span>
        </div>

        <div className="photos-filters" aria-label="写真の種類で絞り込み">
          <button type="button" className={filter === "all" ? "active" : ""} onClick={() => selectFilter("all")}>すべて</button>
          {(Object.keys(labels) as MediaCategory[]).map((key) => (
            <button type="button" key={key} className={filter === key ? "active" : ""} onClick={() => selectFilter(key)}>
              {labels[key]}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="photos-skeleton-grid" aria-label="写真を読み込んでいます">
            {Array.from({ length: 6 }, (_, index) => <span key={index} />)}
          </div>
        ) : visible.length === 0 ? (
          <div className="photos-empty">
            <span aria-hidden="true">▣</span>
            <strong>まだ写真がありません</strong>
            <p>一枚だけでも大丈夫。今日の記録から始められます。</p>
          </div>
        ) : (
          <>
            <div className="photos-grid">
              {displayed.map((item) => (
                <article className="photos-card" key={item.fileKey}>
                  <a href={photoUrl(item.fileKey)} target="_blank" rel="noreferrer">
                    <img
                      src={`${photoUrl(item.fileKey)}?v=${media.length}`}
                      alt={item.filename || labels[item.category]}
                      loading="lazy"
                      decoding="async"
                    />
                  </a>
                  <div>
                    <span className="photos-category">{labels[item.category]}</span>
                    <strong title={item.filename || undefined}>{item.filename || "名称なし"}</strong>
                    <small>{photoDate(item.createdAt)} ・ {fileSize(item.fileSize)}</small>
                    <button type="button" disabled={deleting === item.fileKey} onClick={() => void remove(item)}>
                      {deleting === item.fileKey ? "削除中…" : "削除"}
                    </button>
                  </div>
                </article>
              ))}
            </div>
            {displayed.length < visible.length && (
              <button
                className="photos-load-more"
                type="button"
                onClick={() => setDisplayLimit((current) => current + PAGE_SIZE)}
              >
                次の{Math.min(PAGE_SIZE, visible.length - displayed.length)}枚を表示
              </button>
            )}
          </>
        )}
      </section>
    </>
  );
}
